import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import axios from 'axios';
import {httpDelete, httpGet, httpPatch, httpPost} from '../http.service';
import {QUERY_KEYS} from '../../utils/queryKeys';

const BASE = 'editor/presentationSlugs';

export interface PresentationSlugParam {
    key: string;
    value: string;
}

export interface PresentationSlug {
    _id: string;
    slug: string;
    presentationId: string;
    params: PresentationSlugParam[];
    isFixed?: boolean;
    created?: string;
    lastModified?: string;
}

export interface CreateSlugPayload {
    presentationId: string;
    slug: string;
    params?: PresentationSlugParam[];
}

export interface CreateAutoSlugPayload {
    presentationId: string;
    params?: PresentationSlugParam[];
}

export interface UpdateSlugPayload {
    slug?: string;
    params?: PresentationSlugParam[];
}

export function isSlugConflictError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 409;
}

export function useListSlugs(presentationId: string): UseQueryResult<PresentationSlug[]> {
    return useQuery(
        QUERY_KEYS.presentationSlug.byPresentation(presentationId),
        () => httpGet<PresentationSlug[]>(`${BASE}/${presentationId}`),
        {
            enabled: !!presentationId,
        }
    );
}

export function useCreateSlug(): UseMutationResult<PresentationSlug, unknown, CreateSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: CreateSlugPayload) => httpPost<PresentationSlug>(BASE, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.all);
        },
    });
}

export function useCreateAutoSlug(): UseMutationResult<PresentationSlug, unknown, CreateAutoSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: CreateAutoSlugPayload) => httpPost<PresentationSlug>(`${BASE}/auto`, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.all);
            queryClient.invalidateQueries(QUERY_KEYS.presentationParam.all);
        },
    });
}

export function useUpdateSlug(slugId: string): UseMutationResult<PresentationSlug, unknown, UpdateSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: UpdateSlugPayload) => httpPatch<PresentationSlug>(`${BASE}/${slugId}`, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.all);
        },
    });
}

export function useDeleteSlug(): UseMutationResult<void, unknown, string> {
    const queryClient = useQueryClient();
    return useMutation((slugId: string) => httpDelete<void>(`${BASE}/${slugId}`, {}), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.presentationSlug.all);
        },
    });
}

export function useCheckSlugAvailable(slug: string, excludeId?: string): UseQueryResult<boolean> {
    return useQuery(
        QUERY_KEYS.presentationSlug.check(slug, excludeId),
        () =>
            httpGet<{available: boolean}>(`${BASE}/check`, {params: {slug, ...(excludeId ? {excludeId} : {})}}).then(
                (res) => res.available
            ),
        {
            enabled: !!slug,
            staleTime: 0,
            cacheTime: 0,
            retry: false,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        }
    );
}
